// Motion tokens for the v2 UI — the single source of truth for animation, the
// same way tokens.ts is for color. Motion here is purposeful: it marks that
// something deployed, changed, or is loading. It never decorates.
//
// Everything that moves must spread `reduceMotionSx` into its `sx` (or honor
// `useReducedMotion()` for JS-driven motion) so the dashboard stays calm for
// users who opt out at the OS level.

import { keyframes } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';

// Durations (ms). Keep the vocabulary small so motion feels coherent.
export const duration = {
  fast: 120,
  base: 200,
  slow: 320,
} as const;

// Easing curves. `standard` for state changes, `emphasized` for entrances.
export const easing = {
  standard: 'cubic-bezier(0.2, 0, 0, 1)',
  emphasized: 'cubic-bezier(0.16, 1, 0.3, 1)',
} as const;

/** Entrance: rise 6px and fade in. Used for page and row mounts. */
export const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

/** Grow horizontally from the left — used to "fill" completed pipeline connectors. */
export const growX = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

/**
 * One-shot highlight that fades a tinted background back to transparent — marks
 * a row that just changed via a live update. The tint is passed in so the flash
 * can match the new status hue (green/red/blue).
 */
export const flash = (tint: string) => keyframes`
  0% { background-color: ${tint}; }
  100% { background-color: transparent; }
`;

/** A standard transition string for hover/state changes. */
export const transition = (
  props: string | string[],
  ms: number = duration.base,
  ease: string = easing.standard
): string =>
  (Array.isArray(props) ? props : [props]).map(p => `${p} ${ms}ms ${ease}`).join(', ');

/**
 * sx fragment that disables motion under `prefers-reduced-motion: reduce`.
 * Spread it into any `sx` that animates or transitions.
 */
export const reduceMotionSx = {
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none !important',
    transition: 'none !important',
    transform: 'none !important',
  },
} as const;

/** True when the user asked the OS to minimize motion. */
export const useReducedMotion = (): boolean =>
  useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });
